<template>
  <div class="grid grid-cols-[3rem_1fr_3rem] gap-2">
    <NamiButton
      buttonType="secondary"
      @buttonClick="emit('prevChapter')"
      :icon="IconChevronLeft"
      :disabled="!chapterIdSequence || !aggregate || loading || !hasPrevChapter"
      block
    />
    <SelectSingleList
      v-model="currentChapterId"
      :options="mappedChapterList"
      :disabled="!chapterIdSequence || !aggregate || loading"
    >
      <template #option="{ active, option, selected }">
        <SelectSingleListOption
          :active="active"
          :option="option"
          :selected="selected"
        >
          <template #icon>
            <span
              class="inline-block h-5 w-5"
              :class="{
                'text-nami-comi-blue': !chapterLocks?.[option.value],
                'text-yellow-600 dark:text-yellow-400':
                  !chapterLocks?.[option.value] && accessLocks?.[option.value],
              }"
            >
              <IconCheck v-if="selected" aria-hidden="true" />
              <IconCrown
                v-else-if="chapterLocks?.[option.value] === false"
                aria-hidden="true"
              />
            </span>
          </template>
        </SelectSingleListOption>
      </template>
    </SelectSingleList>
    <NamiButton
      buttonType="secondary"
      @buttonClick="emit('nextChapter')"
      :icon="IconChevronRight"
      :disabled="!chapterIdSequence || !aggregate || loading || !hasNextChapter"
      block
    />
  </div>
</template>

<script setup lang="ts">
import {
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconCrown,
} from "@tabler/icons-vue";
import type { UnwrapNestedRefs } from "vue";
import { ReaderManager } from "~/src/reader/managers/reader";
import { getChapterName } from "~/utils/display/chapter";
import type { ReaderSettingsInterface } from "~/src/reader/static";
import {
  isTitle,
  type TitleChapters,
  type TitleEntity,
  type TitleReadingMode,
} from "~/src/api";
import { getReaderChapter } from "~/src/reader/managers/chapter";
import {
  getReaderTitle,
  getReaderTitleChapters,
} from "~/src/reader/managers/title";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

const {
  aggregate,
  title,
  chapterIdSequence,
  loading,
  currentChapterIndex = 0,
} = defineProps<{
  currentChapterIndex?: number;
  chapterIdSequence?: string[];
  hasNextChapter?: boolean;
  hasPrevChapter?: boolean;
  aggregate?: TitleChapters;
  title?: TitleEntity;
  loading?: boolean;
}>();
const emit = defineEmits<{
  (e: "switchChapter", chapterId: string): void;

  (e: "nextChapter"): void;

  (e: "prevChapter"): void;
}>();
const currentChapterId = computed({
  get() {
    return chapterIdSequence?.[currentChapterIndex] ?? "";
  },
  set(v) {
    emit("switchChapter", v);
  },
});

// const queryClient = useQueryClient();

// chapter access
const accessIds = computed(() => chapterIdSequence ?? []);
const { data: chapterLocks, isPending: locksPending } = useQuery(
  useGatingAccess(accessIds, "novel_chapter"),
);
const { data: accessLocks, isPending: accessPending } = useQuery(
  useGatingAccess(accessIds, "novel_chapter", getTokenOrThrow),
);

const mappedChapterList = computed(() => {
  if (!aggregate?.attributes.list.length || !chapterIdSequence?.length)
    return [];
  const allChapters =
    aggregate.attributes.list.flatMap((v) =>
      v.novelChapters.map((ch) => ({ ...ch, volume: v.volume })),
    ) ?? [];
  const volumeKind = title?.attributes.volumeKind ?? undefined;
  const chapterKind = title?.attributes.chapterKind ?? undefined;

  return chapterIdSequence
    .map((chId) => {
      const foundCh = allChapters.find((ch) => ch.id === chId);
      return {
        text: foundCh
          ? createChapterName(
              foundCh.chapter,
              foundCh.volume,
              foundCh.name,
              "short",
              chapterKind,
              volumeKind,
            )
          : chId,
        value: chId,
      };
    })
    .reverse();
});
</script>

<style scoped lang="postcss"></style>
